// Professional Proposal Document Viewer — On-screen rendering component
// Renders the 12-section proposal in professional engineering format

import React from 'react';
import { ProposalDocument, ProposalSection, CoverPageData, EquipmentGroup, ListItem, ConditionRow, AdjustmentDisplayItem, NextStep } from './proposalDocumentGenerator';
import { ValidationReport } from './engineeringValidator';
import { CheckCircle2, AlertTriangle, XCircle, FileText } from 'lucide-react';

// ─── COVER PAGE ──────────────────────────────────────────────────────────────

const CoverPage: React.FC<{ data: CoverPageData }> = ({ data }) => (
  <div className="relative bg-gradient-to-br from-[#0f1729] to-[#1a2744] rounded-xl p-8 md:p-12 text-center border border-slate-700/50 overflow-hidden">
    <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-orange-500 to-orange-600" />
    <div className="relative z-10">
      <h1 className="font-['Oswald'] text-2xl md:text-3xl text-white font-bold tracking-wider uppercase mb-1">EnergiPro Solutions</h1>
      <p className="text-orange-400 text-xs tracking-[4px] uppercase mb-10">Integrated Oilfield Equipment & Field Support</p>
      <h2 className="font-['Oswald'] text-xl md:text-2xl text-white font-bold mb-8 border-b-2 border-orange-500 pb-4 inline-block">{data.title}</h2>
      <div className="mt-8 inline-block text-left">
        <table className="text-sm">
          <tbody>
            <tr><td className="text-slate-500 pr-6 py-1.5 text-xs uppercase tracking-wide font-semibold">Prepared For</td><td className="text-slate-200 py-1.5">{data.client}, {data.company}</td></tr>
            <tr><td className="text-slate-500 pr-6 py-1.5 text-xs uppercase tracking-wide font-semibold">Location</td><td className="text-slate-200 py-1.5">{data.location}</td></tr>
            <tr><td className="text-slate-500 pr-6 py-1.5 text-xs uppercase tracking-wide font-semibold">Date</td><td className="text-slate-200 py-1.5">{data.date}</td></tr>
            <tr><td className="text-slate-500 pr-6 py-1.5 text-xs uppercase tracking-wide font-semibold">Reference</td><td className="text-orange-300 py-1.5 font-mono">{data.referenceId}</td></tr>
            <tr><td className="text-slate-500 pr-6 py-1.5 text-xs uppercase tracking-wide font-semibold">Status</td><td className="text-slate-200 py-1.5">Preliminary Technical Proposal</td></tr>
          </tbody>
        </table>
      </div>
    </div>
    <p className="mt-10 text-slate-600 text-[10px] tracking-wide">CONFIDENTIAL — This document is intended for the named recipient only.</p>
  </div>
);

// ─── SECTION HEADER ──────────────────────────────────────────────────────────

const SectionHeader: React.FC<{ number: number; title: string }> = ({ number, title }) => (
  <div className="flex items-center gap-3 mb-5 pb-3 border-b border-orange-500/30">
    <div className="w-8 h-8 rounded-full bg-orange-500 text-white flex items-center justify-center text-sm font-bold shrink-0">{number}</div>
    <h3 className="font-['Oswald'] text-lg text-white uppercase tracking-wide">{title}</h3>
  </div>
);

// ─── CONTENT RENDERERS ───────────────────────────────────────────────────────

const TextContent: React.FC<{ paragraphs: string[] }> = ({ paragraphs }) => (
  <div className="space-y-3">
    {paragraphs.map((p, i) => (
      <p key={i} className="text-slate-300 text-sm leading-relaxed">{p}</p>
    ))}
  </div>
);

const ConditionsTable: React.FC<{ rows: ConditionRow[] }> = ({ rows }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b-2 border-slate-600">
          <th className="text-left py-2.5 px-3 text-slate-400 text-xs uppercase tracking-wide font-semibold">Parameter</th>
          <th className="text-left py-2.5 px-3 text-slate-400 text-xs uppercase tracking-wide font-semibold">Value</th>
          <th className="text-left py-2.5 px-3 text-slate-400 text-xs uppercase tracking-wide font-semibold">Unit</th>
          <th className="text-left py-2.5 px-3 text-slate-400 text-xs uppercase tracking-wide font-semibold">Engineering Implication</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <tr key={i} className="border-b border-slate-700/50">
            <td className="py-2.5 px-3 text-white font-medium">{row.parameter}</td>
            <td className="py-2.5 px-3 text-orange-300 font-semibold">{row.value}</td>
            <td className="py-2.5 px-3 text-slate-400">{row.unit}</td>
            <td className="py-2.5 px-3 text-slate-300">{row.implication}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const GroupedEquipment: React.FC<{ groups: EquipmentGroup[] }> = ({ groups }) => (
  <div className="space-y-4">
    {groups.map((group, gi) => (
      <div key={gi} className="bg-slate-800/30 rounded-lg overflow-hidden border border-slate-700/40">
        <div className="px-4 py-2.5 bg-slate-800/60 border-b border-slate-700/40 border-l-4 border-l-orange-500">
          <h4 className="text-white text-sm font-semibold">{group.system}</h4>
        </div>
        <div className="p-4 space-y-1.5">
          {group.items.map((item, ii) => (
            <div key={ii} className="flex items-start gap-2 text-sm">
              <span className="text-orange-400 mt-0.5">▸</span>
              <span className="text-white">{item.equipment}</span>
              <span className="text-slate-500">—</span>
              <span className="text-slate-400">{item.specification}</span>
            </div>
          ))}
        </div>
      </div>
    ))}
  </div>
);

const EquipmentTable: React.FC<{ headers: string[]; rows: string[][] }> = ({ headers, rows }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b-2 border-slate-600">
          {headers.map((h, i) => (
            <th key={i} className="text-left py-2.5 px-3 text-slate-400 text-xs uppercase tracking-wide font-semibold">{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, ri) => (
          <tr key={ri} className="border-b border-slate-700/50">
            {row.map((cell, ci) => (
              <td key={ci} className={`py-2 px-3 ${ci === 0 ? 'text-slate-400 text-xs uppercase font-medium' : ci === 1 ? 'text-white font-medium' : 'text-slate-300'}`}>
                {cell}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const ListContent: React.FC<{ items: ListItem[] }> = ({ items }) => (
  <div className="space-y-2">
    {items.map((item, i) => (
      <div key={i} className={`flex items-start gap-2.5 text-sm px-3 py-2 rounded ${
        item.type === 'warning' ? 'bg-amber-500/5 border border-amber-500/20' :
        item.type === 'info' ? 'bg-blue-500/5 border border-blue-500/20' : ''
      }`}>
        <span className={`mt-0.5 shrink-0 ${
          item.type === 'warning' ? 'text-amber-400' : item.type === 'info' ? 'text-blue-400' : 'text-orange-400'
        }`}>
          {item.type === 'warning' ? '⚠' : item.type === 'info' ? 'ℹ' : '▪'}
        </span>
        <span className="text-slate-300 leading-relaxed">{item.text}</span>
      </div>
    ))}
  </div>
);

const AdjustmentsContent: React.FC<{ items: AdjustmentDisplayItem[] }> = ({ items }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b-2 border-slate-600">
          <th className="text-left py-2.5 px-3 text-slate-400 text-xs uppercase tracking-wide font-semibold">Equipment</th>
          <th className="text-left py-2.5 px-3 text-slate-400 text-xs uppercase tracking-wide font-semibold">Action</th>
          <th className="text-left py-2.5 px-3 text-slate-400 text-xs uppercase tracking-wide font-semibold">Status</th>
          <th className="text-left py-2.5 px-3 text-slate-400 text-xs uppercase tracking-wide font-semibold">Note</th>
        </tr>
      </thead>
      <tbody>
        {items.map((item, i) => (
          <tr key={i} className="border-b border-slate-700/50">
            <td className="py-2.5 px-3 text-white font-medium">{item.equipment}</td>
            <td className="py-2.5 px-3 text-slate-300">{item.action}</td>
            <td className="py-2.5 px-3">
              <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${
                item.status === 'Accepted' ? 'bg-green-500/10 border border-green-500/40 text-green-300' :
                item.status === 'Conditional' ? 'bg-amber-500/10 border border-amber-500/40 text-amber-300' :
                'bg-red-500/10 border border-red-500/40 text-red-300'
              }`}>{item.status}</span>
            </td>
            <td className="py-2.5 px-3 text-slate-400 text-xs">{item.note}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const ValidationSummaryContent: React.FC<{ report: ValidationReport }> = ({ report }) => (
  <div className="space-y-3">
    <div className={`flex items-center gap-3 px-4 py-3 rounded-lg border ${
      report.overallStatus === 'Approved' ? 'bg-green-500/5 border-green-500/30' :
      report.overallStatus === 'Conditional' ? 'bg-amber-500/5 border-amber-500/30' :
      'bg-red-500/5 border-red-500/30'
    }`}>
      {report.overallStatus === 'Approved' ? <CheckCircle2 className="w-5 h-5 text-green-400" /> :
       report.overallStatus === 'Conditional' ? <AlertTriangle className="w-5 h-5 text-amber-400" /> :
       <XCircle className="w-5 h-5 text-red-400" />}
      <span className={`text-sm font-semibold ${
        report.overallStatus === 'Approved' ? 'text-green-300' :
        report.overallStatus === 'Conditional' ? 'text-amber-300' : 'text-red-300'
      }`}>Overall Status: {report.overallStatus}</span>
    </div>
    <div className="flex items-center gap-4 text-xs">
      {report.acceptedChanges.length > 0 && <span className="text-green-300">✓ {report.acceptedChanges.length} Accepted</span>}
      {report.conditionalChanges.length > 0 && <span className="text-amber-300">⚠ {report.conditionalChanges.length} Conditional</span>}
      {report.rejectedChanges.length > 0 && <span className="text-red-300">✕ {report.rejectedChanges.length} Rejected</span>}
    </div>
    <p className="text-slate-400 text-xs italic leading-relaxed mt-2">{report.engineeringRecommendation.split('\n')[0]}</p>
  </div>
);

const NextStepsContent: React.FC<{ steps: NextStep[] }> = ({ steps }) => (
  <div className="space-y-3">
    {steps.map((step, i) => (
      <div key={i} className="flex items-start gap-3 py-2 border-b border-slate-700/30 last:border-0">
        <div className="w-7 h-7 rounded-full bg-slate-700 text-white flex items-center justify-center text-xs font-bold shrink-0">{step.step}</div>
        <div className="flex-1">
          <p className="text-white text-sm font-medium">{step.action}</p>
          <p className="text-slate-500 text-xs">{step.timeline}</p>
        </div>
      </div>
    ))}
  </div>
);

// ─── MAIN COMPONENT ──────────────────────────────────────────────────────────

interface ProposalDocumentViewerProps {
  proposal: ProposalDocument;
}

export const ProposalDocumentViewer: React.FC<ProposalDocumentViewerProps> = ({ proposal }) => {
  return (
    <div className="space-y-8">
      {proposal.sections.map((section, idx) => {
        if (section.content.type === 'cover') {
          return <CoverPage key={section.id} data={(section.content as { type: 'cover'; data: CoverPageData }).data} />;
        }

        return (
          <div key={section.id} className="bg-[#111827] border border-slate-700/50 rounded-xl p-6 md:p-8">
            <SectionHeader number={section.number} title={section.title} />
            {section.content.type === 'text' && <TextContent paragraphs={(section.content as any).paragraphs} />}
            {section.content.type === 'conditions-table' && <ConditionsTable rows={(section.content as any).rows} />}
            {section.content.type === 'grouped-equipment' && <GroupedEquipment groups={(section.content as any).groups} />}
            {section.content.type === 'table' && <EquipmentTable headers={(section.content as any).headers} rows={(section.content as any).rows} />}
            {section.content.type === 'list' && <ListContent items={(section.content as any).items} />}
            {section.content.type === 'adjustments' && <AdjustmentsContent items={(section.content as any).items} />}
            {section.content.type === 'validation-summary' && <ValidationSummaryContent report={(section.content as any).report} />}
            {section.content.type === 'next-steps' && <NextStepsContent steps={(section.content as any).steps} />}
          </div>
        );
      })}

      {/* Document Footer */}
      <div className="text-center py-4 border-t border-slate-700/50">
        <p className="text-slate-500 text-xs">
          Reference: {proposal.metadata.referenceId} | Generated: {new Date(proposal.metadata.generatedAt).toLocaleDateString('en-GB')}
        </p>
        <p className="text-slate-600 text-[10px] mt-1">
          PRELIMINARY — This document constitutes a technical assessment and does not represent a commercial offer or binding commitment.
        </p>
      </div>
    </div>
  );
};
